/**
 * MFASetup.tsx
 *
 * Admin/user settings panel for enabling TOTP-based MFA.
 * Rendered inside Dashboard when the user opens their account settings.
 *
 * Flow:
 *  1. User clicks "Enable MFA"
 *  2. Component calls POST /mfa/setup → gets QR code + secret
 *  3. User scans QR code in Google Authenticator / Authy
 *  4. User enters the 6-digit code → POST /mfa/verify
 *  5. MFA is now active — shown in status
 */

import React, { useEffect, useState } from 'react';

interface MFASetupProps {
  token: string;
  onStatusChange?: (enabled: boolean) => void;
}

const API = 'https://coast-terminal-production.up.railway.app';

const MFASetup: React.FC<MFASetupProps> = ({ token, onStatusChange }) => {
  // Design tokens
  const gold = '#C9A96E';
  const goldDim = 'rgba(201,169,110,0.1)';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const bg = '#080d18';
  const surface = '#0e1420';
  const surfaceRaised = '#141d2e';
  const surfaceHigh = '#1a2438';
  const border = '#1e2a3d';
  const borderLight = '#243448';
  const textPrimary = '#E8DCC8';
  const textSecondary = '#7A8CA0';
  const textMuted = '#3A4A5C';
  const serifFont = "'Cormorant Garamond', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState<{ mfa_enabled: boolean; mfa_verified: boolean; mfa_setup_pending: boolean } | null>(null);
  const [setupData, setSetupData] = useState<{ secret: string; qr_code: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState<'status' | 'setup' | 'verify'>('status');

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/mfa/status`, { headers });
      const data = await res.json();
      setStatus(data);
    } catch {
      setError('Failed to load MFA status.');
    }
  };

  useEffect(() => { fetchStatus(); }, []); // eslint-disable-line

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/mfa/setup`, { method: 'POST', headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Setup failed');
      }
      const data = await res.json();
      setSetupData(data);
      setStep('verify');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/mfa/verify`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Invalid code');
      }
      setSuccess('MFA is now active. Your account is secured.');
      setStep('status');
      setSetupData(null);
      setCode('');
      await fetchStatus();
      onStatusChange?.(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will make your account less secure.')) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/mfa/disable`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed to disable MFA');
      setSuccess('MFA has been disabled.');
      await fetchStatus();
      onStatusChange?.(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inp: React.CSSProperties = {
    backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, borderRadius: '2px',
    padding: '11px 14px', color: textPrimary, fontSize: '20px', outline: 'none',
    fontFamily: 'monospace', letterSpacing: '8px', textAlign: 'center', width: '100%',
    boxSizing: 'border-box',
  };

  const btnGold: React.CSSProperties = {
    backgroundColor: gold, color: bg, border: 'none', borderRadius: '2px',
    padding: '11px 28px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
  };

  const btnOut: React.CSSProperties = {
    backgroundColor: 'transparent', color: textSecondary, border: `1px solid ${borderLight}`,
    borderRadius: '2px', padding: '11px 20px', fontWeight: 700, fontSize: '11px',
    letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>
        Account Security
      </div>
      <div style={{ fontSize: '22px', fontWeight: 600, color: '#082f47', fontFamily: serifFont, marginBottom: '20px' }}>
        Two-Factor Authentication
      </div>

      {/* Status banner */}
      {status && (
        <div style={{
          padding: '14px 16px', marginBottom: '20px',
          backgroundColor: status.mfa_enabled ? 'rgba(26,122,74,0.1)' : 'rgba(201,169,110,0.06)',
          border: `1px solid ${status.mfa_enabled ? 'rgba(26,122,74,0.3)' : borderLight}`,
          borderLeft: `3px solid ${status.mfa_enabled ? green : gold}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: status.mfa_enabled ? green : gold, marginBottom: '3px' }}>
              {status.mfa_enabled ? '✓ MFA Active' : 'MFA Not Enabled'}
            </div>
            <div style={{ fontSize: '11px', color: textMuted }}>
              {status.mfa_enabled
                ? 'Your account requires a 6-digit code from your authenticator app at each login.'
                : 'Enable MFA to add a second layer of security to your account.'}
            </div>
          </div>
          {status.mfa_enabled && (
            <button onClick={handleDisable} disabled={loading} style={{ ...btnOut, color: red, borderColor: red, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Disable
            </button>
          )}
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: '14px', backgroundColor: 'rgba(204,34,0,0.08)', border: `1px solid rgba(204,34,0,0.25)`, color: red, fontSize: '12px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: '10px 14px', marginBottom: '14px', backgroundColor: 'rgba(26,122,74,0.08)', border: `1px solid rgba(26,122,74,0.25)`, color: green, fontSize: '12px' }}>
          {success}
        </div>
      )}

      {/* Setup step — show QR code */}
      {step === 'verify' && setupData && (
        <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '24px' }}>
          <div style={{ fontSize: '11px', color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '16px' }}>
            Step 1 — Scan this QR code
          </div>
          <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '16px', fontFamily: serifFont }}>
            Open Google Authenticator or Authy and scan the QR code below.
          </div>

          {/* QR Code */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '2px', display: 'inline-block' }}>
              <img src={setupData.qr_code} alt="MFA QR Code" style={{ width: '180px', height: '180px', display: 'block' }} />
            </div>
          </div>

          {/* Manual entry */}
          <div style={{ backgroundColor: surfaceRaised, border: `1px solid ${border}`, padding: '12px 14px', marginBottom: '20px' }}>
            <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>
              Can't scan? Enter manually:
            </div>
            <div style={{ fontSize: '13px', color: gold, fontFamily: 'monospace', letterSpacing: '2px', wordBreak: 'break-all' }}>
              {setupData.secret}
            </div>
          </div>

          <div style={{ fontSize: '11px', color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px' }}>
            Step 2 — Enter the 6-digit code
          </div>
          <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '14px', fontFamily: serifFont }}>
            Enter the code shown in your authenticator app to confirm setup.
          </div>

          <input
            style={inp}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={e => e.key === 'Enter' && handleVerify()}
            maxLength={6}
            autoFocus
          />

          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            <button onClick={() => { setStep('status'); setSetupData(null); setCode(''); setError(''); }} style={btnOut}>
              Cancel
            </button>
            <button onClick={handleVerify} disabled={loading || code.length !== 6} style={{ ...btnGold, opacity: code.length === 6 ? 1 : 0.4 }}>
              {loading ? 'Verifying...' : 'Activate MFA'}
            </button>
          </div>
        </div>
      )}

      {/* Enable button — only shown when MFA is off and not in setup */}
      {step === 'status' && status && !status.mfa_enabled && (
        <button onClick={handleSetup} disabled={loading} style={btnGold}>
          {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
        </button>
      )}
    </div>
  );
};

export default MFASetup;